/* eslint-disable arrow-body-style */
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  CircularProgress,
  Typography,
  TableSortLabel,
  Chip,
  alpha,
  Box,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import { LightTooltip } from '../custom-components/custom-tooltip';
import Iconify from '../iconify';

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  if (orderBy === 'send_time') {
    return new Date(b[orderBy]) - new Date(a[orderBy]);
  }
  if (orderBy === 'metadata.doctor_name') {
    const aValue = a.metadata?.doctor_name || '';
    const bValue = b.metadata?.doctor_name || '';
    return bValue.localeCompare(aValue);
  }
  const aValue = a[orderBy] || '';
  const bValue = b[orderBy] || '';
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
};

export default function EmailTrackingTable({
  emails,
  loading,
  order,
  orderBy,
  page,
  rowsPerPage,
  onRequestSort,
  onChangePage,
  onChangeRowsPerPage,
  onViewDetails,
}) {
  const filteredAndSortedEmails = React.useMemo(() => {
    return [...emails].sort(getComparator(order, orderBy));
  }, [emails, order, orderBy]);

  const paginatedEmails = filteredAndSortedEmails.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredAndSortedEmails.length) : 0;

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'send_time'}
                  direction={orderBy === 'send_time' ? order : 'asc'}
                  onClick={() => onRequestSort('send_time')}
                >
                  Sent At
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'metadata.doctor_name'}
                  direction={orderBy === 'metadata.doctor_name' ? order : 'asc'}
                  onClick={() => onRequestSort('metadata.doctor_name')}
                >
                  Doctor
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'subject'}
                  direction={orderBy === 'subject' ? order : 'asc'}
                  onClick={() => onRequestSort('subject')}
                >
                  Subject
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'to'}
                  direction={orderBy === 'to' ? order : 'asc'}
                  onClick={() => onRequestSort('to')}
                >
                  To
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => onRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'opens'}
                  direction={orderBy === 'opens' ? order : 'asc'}
                  onClick={() => onRequestSort('opens')}
                >
                  Opens
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'clicks'}
                  direction={orderBy === 'clicks' ? order : 'asc'}
                  onClick={() => onRequestSort('clicks')}
                >
                  Clicks
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell align="center" colSpan={8} sx={{ py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading email tracking data...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredAndSortedEmails.length === 0 ? (
              <TableRow>
                <TableCell align="center" colSpan={8} sx={{ py: 10 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Iconify
                      icon="eva:email-outline"
                      sx={{ mb: 2, width: 48, height: 48, color: 'text.disabled' }}
                    />
                    <Typography variant="h6" gutterBottom>
                      No Emails Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Try adjusting your search or filters to find what you're looking for.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEmails.map((email) => (
                <TableRow
                  key={email.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {dayjs(email.send_time).format('MMM D, YYYY')}
                      <br />
                      {dayjs(email.send_time).format('HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" noWrap>
                      {email?.metadata?.doctor_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {email.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>{email.to}</TableCell>
                  <TableCell>
                    <Chip
                      label={email.status}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.65rem',
                        backgroundColor: (theme) => {
                          const emailStatus = email.status?.toLowerCase();
                          switch (emailStatus) {
                            case 'sent':
                              return alpha(theme.palette.success.main, 0.12);
                            case 'bounced':
                              return alpha(theme.palette.warning.main, 0.12);
                            case 'rejected':
                              return alpha(theme.palette.error.main, 0.12);
                            default:
                              return theme.palette.action.selected;
                          }
                        },
                        color: (theme) => {
                          const emailStatus = email.status?.toLowerCase();
                          switch (emailStatus) {
                            case 'sent':
                              return theme.palette.success.main;
                            case 'bounced':
                              return theme.palette.warning.main;
                            case 'rejected':
                              return theme.palette.error.main;
                            default:
                              return theme.palette.text.primary;
                          }
                        },
                        border: (theme) => {
                          const emailStatus = email.status?.toLowerCase();
                          let color;
                          switch (emailStatus) {
                            case 'sent':
                              color = theme.palette.success.main;
                              break;
                            case 'bounced':
                              color = theme.palette.warning.main;
                              break;
                            case 'rejected':
                              color = theme.palette.error.main;
                              break;
                            default:
                              color = theme.palette.divider;
                          }
                          return `1px solid ${alpha(color, 0.24)}`;
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {email.opens}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {email.clicks}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <LightTooltip title="View detailed email tracking info.">
                      <IconButton color="primary" onClick={() => onViewDetails(email)}>
                        <VisibilityIcon />
                      </IconButton>
                    </LightTooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
            {!loading && filteredAndSortedEmails.length > 0 && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={8} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredAndSortedEmails.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
}
